// NPM MODULES
import React, { useEffect, useCallback, useState, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';

// OUR COMPONENTS
import SingleTraceLog from '../../components/admin/traceLogs/SingleTraceLog';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';
import DateRangeSelector from '../../components/common/DateRangeSelector';
import Loader from '../../components/common/Loader';
import Layout from '../../components/layouts/Layout';
import Paginator from '../../components/common/Paginator';

// ROUTES
import { AdminRoutes } from '../../routes/AdminRoutes';

// FORMATTED MESSAGES
import TraceLogsFormattedMessages from '../../messages/admin/TraceLogFormattedMessages';

// REQUEST
import useTraceLogSearch from '../../requests/TraceLogRequest';

// CONFIG
import config from '../../Config';

const Trace = ({ errorHandler, accessLevel, accessType }) => {
  const { searchTraceLog } = useTraceLogSearch();
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSearching, setIsSearching] = useState(true);
  const [traceLogs, setTraceLogs] = useState([]);
  const [paginatedLogs, setPaginatedLogs] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(true);
  const [logLevelFilter, setLogLevelFilter] = useState(null);
  const [logGroupIdFilter, setLogGroupIdFilter] = useState(null);
  const [processNameFilter, setProcessNameFilter] = useState(null);
  const [programNameFilter, setProgramNameFilter] = useState(null);
  const [logMessageFilter, setLogMessageFilter] = useState(null);
  const [tagFilter, setTagFilter] = useState(null);
  const [currentlySearching, setCurrentlySearching] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [selected, setSelected] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filteredText, setFilteredText] = useState([]);
  const [reset, setReset] = useState(false);
  const [timeRange, setTimeRange] = useState(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    setPaginatedResults(traceLogs);
  }, [traceLogs, pageNumber, pageLength]);

  const setPaginatedResults = (logs) => {
    const paginatedLogs = logs.slice(
      (pageNumber - 1) * pageLength,
      (pageNumber - 1) * pageLength + pageLength
    );
    setPaginatedLogs(paginatedLogs);
  };

  const handleTraceLogSearch = useCallback(async () => {
    if (fromDate && toDate) {
      try {
        setIsSearching(true);
        const searchParams = {
          fromDate: fromDate,
          toDate: toDate,
          timeRange: timeRange,
          timeRangeStart: fromDate,
          timeRangeEnd: toDate,
          logLevelFilter: logLevelFilter,
          logGroupIdFilter: logGroupIdFilter,
          processNameFilter: processNameFilter,
          programNameFilter: programNameFilter,
          logMessageFilter: logMessageFilter,
          tagFilter: tagFilter,
        };

        const traceLogs = await searchTraceLog(searchParams, errorHandler);
        console.log('Got POST result:', traceLogs);
        setTraceLogs(traceLogs || []);
        setCurrentlySearching(searchParams);
        setIsSearching(false);
        setPageNumber(1);
      } catch (e) {
        console.error(e);
        setTraceLogs([]);
        setIsSearching(false);
        setHasError(true);
        setErrorMessage(e.message);
      }
    }
  }, [toDate, fromDate, reset]);

  useEffect(() => {
    if (fromDate && toDate) handleTraceLogSearch();
  }, [handleTraceLogSearch]);

  const backButtonClick = () => {
    setSelected(null);
  };

  const toggleMenu = () => {
    setFilterOpen(!filterOpen);
  };

  const onClickMessage = (error) => {
    setSelected(error);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  const changeEvent = (i) => {
    setPageNumber(i);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    switch (name) {
      case 'logLevelFilter':
        setLogLevelFilter(newValue);
        break;
      case 'logGroupIdFilter':
        setLogGroupIdFilter(newValue);
        break;
      case 'processNameFilter':
        setProcessNameFilter(newValue);
        break;
      case 'programNameFilter':
        setProgramNameFilter(newValue);
        break;
      case 'logMessageFilter':
        setLogMessageFilter(newValue);
        break;
      case 'tagFilter':
        setTagFilter(newValue);
        break;
      default:
        break;
    }

    // Clear any existing timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Set new timer to trigger search after 500ms
    debounceTimer.current = setTimeout(() => {
      setPageNumber(1);
      setReset((prev) => !prev); // Toggle reset to trigger effect
    }, 500);
  };

  const clearFilters = () => {
    setLogLevelFilter(null);
    setLogGroupIdFilter(null);
    setProcessNameFilter(null);
    setProgramNameFilter(null);
    setLogMessageFilter(null);
    setTagFilter(null);
    setCurrentlySearching({});
    document.getElementById('filter-form').reset();
  };

  /*
      Generates the Search Tools for querying the data
    */
  const generateSearchText = useCallback(() => {
    // Destructures which filters the table is currently displaying fields on.
    const {
      logLevelFilter,
      logGroupIdFilter,
      processNameFilter,
      programNameFilter,
      logMessageFilter,
      tagFilter,
    } = currentlySearching || {};

    const filterText = [];
    const filterValues = [
      'Log Level:',
      'Log Group ID:',
      'Process Name:',
      'Program Name:',
      'Log Message:',
      'Tag:',
    ];

    [
      logLevelFilter,
      logGroupIdFilter,
      processNameFilter,
      programNameFilter,
      logMessageFilter,
      tagFilter,
    ].forEach((filter, i) => {
      if (filter && filter !== '')
        filterText.push(
          <span key={i}>
            {filterText && filterText.length === 0 ? '' : ', '}
            <strong>{filterValues[i]}</strong> {filter}
          </span>
        );
    });

    setFilteredText(filterText);
  }, [currentlySearching]);

  useEffect(() => {
    generateSearchText();
  }, [generateSearchText]);

  if (accessLevel !== '3' || accessType !== '3') return null;
  if (selected)
    return (
      <SingleTraceLog
        traceLog={selected}
        backButtonClick={() => setSelected(null)}
        accessLevel={accessLevel}
        accessType={accessType}
      />
    );

  return (
    <Layout
      routes={AdminRoutes}
      title={
        selected
          ? 'Trace Log Details'
          : TraceLogsFormattedMessages.traceLogTitle
      }
      accessLevel={accessLevel}
      accessType={accessType}
      backButtonText={selected ? TraceLogsFormattedMessages.backText : null}
      handleBackButton={() => setSelected(null)}
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <DateRangeSelector
            key={selectorKeyRefresh ? 1 : 0}
            handleDateSearch={(fromDate, toDate, timeRange) =>
              handleTraceLogSearch(fromDate, toDate, timeRange)
            }
            parentStartDate={fromDate}
            setParentStartDate={setFromDate}
            parentEndDate={toDate}
            setParentEndDate={setToDate}
          />
          <AdvancedFiltersContainer open={filterOpen} setOpen={setFilterOpen}>
            <Box component="form" id="trace-filter-form">
              <Stack
                sx={{
                  flexDirection: { lg: 'row' },
                  gap: 2,
                  marginY: 2,
                  maxWidth: { lg: 674 },
                }}
              >
                <Stack>
                  <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                    Filter Logs:
                  </Typography>
                </Stack>

                <Stack sx={{ gap: 1, flex: 1 }}>
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Log Level"
                      name="logLevelFilter"
                      value={logLevelFilter || ''}
                      onChange={(e) => handleInputChange(e)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Log Group ID"
                      value={logGroupIdFilter || ''}
                      name="logGroupIdFilter"
                      onChange={(e) => handleInputChange(e)}
                    />
                  </Stack>

                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Process Name"
                      name="processNameFilter"
                      value={processNameFilter || ''}
                      onChange={(e) => handleInputChange(e)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Program Name"
                      value={programNameFilter || ''}
                      name="programNameFilter"
                      onChange={(e) => handleInputChange(e)}
                    />
                  </Stack>

                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Log Message"
                      name="logMessageFilter"
                      value={logMessageFilter || ''}
                      onChange={(e) => handleInputChange(e)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Tag"
                      name="tagFilter"
                      value={tagFilter || ''}
                      onChange={(e) => handleInputChange(e)}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                maxWidth: { lg: 674 },
              }}
            >
              {filteredText && filteredText.length > 0 && (
                <Button
                  color="secondary"
                  onClick={clearFilters}
                  sx={{
                    textTransform: 'none',
                    textDecoration: 'underline',
                    fontWeight: 400,
                  }}
                >
                  {TraceLogsFormattedMessages.clear}
                </Button>
              )}
            </Stack>
          </AdvancedFiltersContainer>
        </Grid>
        <Grid size={12}>
          {isSearching ? (
            <Loader />
          ) : (
            <>
              {paginatedLogs.length === 0 ? (
                <Typography>No results found.</Typography>
              ) : (
                <>
                  {filteredText && filteredText.length > 0 && (
                    <Typography fontSize={12} marginBottom={1}>
                      {filteredText}
                    </Typography>
                  )}
                  <TableContainer
                    sx={{
                      marginBottom: 4,
                      overflowX: 'auto',
                      width: '100%',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Table stickyHeader sx={{ width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Log Time (UTC)</TableCell>
                          <TableCell>Log Level</TableCell>
                          <TableCell>Log Group Id</TableCell>
                          <TableCell>Process Name</TableCell>
                          <TableCell>Program Name</TableCell>
                          <TableCell>Log Message</TableCell>
                          <TableCell>Tag</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedLogs.map((log, i) => {
                          const logTime = DateTime.fromISO(log.created_at)
                            .toUTC()
                            .toFormat('dd-LLL-yyyy h:mm:ss a');
                          return (
                            <TableRow key={i}>
                              <TableCell>{logTime}</TableCell>
                              <TableCell>{log.log_level}</TableCell>
                              <TableCell>{log.log_group_id || 'N/A'}</TableCell>
                              <TableCell>{log.process_name}</TableCell>
                              <TableCell>{log.program_name}</TableCell>
                              <TableCell>
                                <Link
                                  tabIndex={0}
                                  onClick={() => setSelected(log)}
                                >
                                  {log.log_message}
                                </Link>
                              </TableCell>
                              <TableCell>{log.tag || 'N/A'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Paginator
                    itemCount={traceLogs.length}
                    changeEvent={changeEvent}
                    pageLength={pageLength}
                    editFunction={changePageLength}
                    pageNumber={pageNumber}
                    showTotal={'Total Logs'}
                  />
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Layout>
  );
};

export default Trace;
